import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Mail, CheckCircle2, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/password-reset/request`, { email: email.trim() });
    } catch (err) {
      // We intentionally never surface server errors here — endpoint always returns 200
      // to prevent account enumeration.
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="shadow-xl border-0" data-testid="forgot-password-card">
          <CardHeader className="text-center pb-2">
            <Link to="/" className="inline-block mb-4">
              <img
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=64&h=64&fit=crop"
                alt="Gleam Guru"
                className="w-16 h-16 rounded-xl mx-auto"
              />
            </Link>
            <CardTitle className="text-2xl font-heading">
              {submitted ? 'Check your inbox' : 'Forgot password?'}
            </CardTitle>
            <CardDescription>
              {submitted
                ? "If an account exists for that email, we've sent a reset link."
                : "Enter your email and we'll send you a link to set a new password."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-4 rounded-lg flex items-start gap-3" data-testid="forgot-password-success">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div className="text-sm leading-relaxed">
                    <p className="font-semibold">Email sent to {email}</p>
                    <p className="mt-1 text-emerald-700">
                      The link expires in 60 minutes. Check your spam folder if you don&apos;t see it within a few minutes.
                    </p>
                  </div>
                </div>
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full" data-testid="forgot-back-to-login">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to sign in
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#F56F05] hover:bg-[#C2410C] text-white font-semibold py-6"
                  disabled={loading}
                  data-testid="forgot-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending link...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Send reset link
                    </>
                  )}
                </Button>

                <Link
                  to="/login"
                  className="block text-center text-sm text-zinc-500 hover:text-[#F56F05] mt-4"
                  data-testid="forgot-back-link"
                >
                  <ArrowLeft className="inline h-3.5 w-3.5 mr-1" />
                  Back to sign in
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
